import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { FiMenu, FiX, FiUser, FiLogOut, FiSettings } from 'react-icons/fi';

const Navbar = () => {
  const { user, logout, isAdmin } = useAuth();
  const navigate = useNavigate();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = () => {
    logout();
    navigate('/');
    setDropdownOpen(false);
  };

  return (
    <nav className={`navbar${scrolled ? ' scrolled' : ''}`}>
      <style>{`
        .navbar {
          position: fixed; top: 0; left: 0; right: 0; z-index: 1000;
          padding: 0 24px; height: 70px;
          display: flex; align-items: center; justify-content: space-between;
          transition: all 0.3s ease;
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(12px);
          -webkit-backdrop-filter: blur(12px);
          border-bottom: 1px solid transparent;
        }
        .navbar.scrolled {
          background: rgba(255,255,255,0.97);
          border-bottom-color: var(--border);
          box-shadow: var(--shadow-sm);
        }
        .nav-inner { max-width: 1180px; margin: 0 auto; width: 100%; display: flex; align-items: center; justify-content: space-between; }
        .nav-logo { display: flex; align-items: center; gap: 10px; font-family: var(--font-display); font-weight: 800; font-size: 22px; color: var(--primary); }
        .nav-logo-icon { width: 36px; height: 36px; background: linear-gradient(135deg, var(--primary), var(--accent)); border-radius: 8px; display: flex; align-items: center; justify-content: center; color: white; font-size: 18px; font-weight: 800; }
        .nav-links { display: flex; align-items: center; gap: 4px; }
        .nav-link { padding: 8px 16px; border-radius: var(--radius); font-size: 15px; font-weight: 500; color: var(--dark-3); transition: var(--transition); }
        .nav-link:hover, .nav-link.active { color: var(--primary); background: var(--primary-light); }
        .nav-actions { display: flex; align-items: center; gap: 12px; }
        .nav-user { position: relative; }
        .nav-user-btn { display: flex; align-items: center; gap: 8px; padding: 8px 14px; border-radius: var(--radius); background: var(--primary-light); color: var(--primary); border: none; cursor: pointer; font-weight: 600; font-size: 14px; transition: var(--transition); font-family: var(--font-display); }
        .nav-user-btn:hover { background: var(--primary); color: white; }
        .nav-dropdown { position: absolute; right: 0; top: calc(100% + 8px); background: white; border-radius: var(--radius-lg); box-shadow: var(--shadow-lg); border: 1px solid var(--border); min-width: 200px; overflow: hidden; animation: fadeInUp 0.2s ease; z-index: 100; }
        .nav-dropdown a, .nav-dropdown button { display: flex; align-items: center; gap: 10px; padding: 12px 16px; font-size: 14px; font-weight: 500; color: var(--dark-3); transition: var(--transition); width: 100%; border: none; background: none; cursor: pointer; text-align: left; }
        .nav-dropdown a:hover, .nav-dropdown button:hover { background: var(--bg); color: var(--primary); }
        .nav-dropdown-divider { height: 1px; background: var(--border); margin: 4px 0; }
        .nav-dropdown .logout-btn { color: var(--error); }
        .nav-dropdown .logout-btn:hover { background: #fee2e2; color: var(--error); }
        .hamburger { display: none; background: none; border: none; color: var(--dark); font-size: 24px; cursor: pointer; padding: 4px; }
        .mobile-nav { display: none; }
        @media (max-width: 900px) {
          .nav-links, .nav-actions { display: none; }
          .hamburger { display: flex; align-items: center; }
          .mobile-nav {
            display: block; position: fixed; top: 70px; left: 0; right: 0;
            background: white; border-bottom: 1px solid var(--border);
            box-shadow: var(--shadow-lg); padding: 16px; z-index: 999;
            animation: fadeInUp 0.2s ease;
          }
          .mobile-nav a, .mobile-nav button { display: flex; align-items: center; gap: 12px; padding: 12px 16px; border-radius: var(--radius); font-size: 15px; font-weight: 500; color: var(--dark-3); margin-bottom: 4px; width: 100%; border: none; background: none; cursor: pointer; transition: var(--transition); }
          .mobile-nav a:hover { background: var(--bg); color: var(--primary); }
          .mobile-nav .mobile-cta { background: var(--primary); color: white; border-radius: var(--radius); text-align: center; justify-content: center; margin-top: 8px; padding: 14px; font-weight: 600; }
          .mobile-divider { height: 1px; background: var(--border); margin: 8px 0; }
        }
      `}</style>
      <div className="nav-inner">
        <Link to="/" className="nav-logo">
          <div className="nav-logo-icon">K</div>
          Korpnex
        </Link>

        <div className="nav-links">
          <NavLink to="/" end className="nav-link">Home</NavLink>
          <NavLink to="/courses" className="nav-link">Courses</NavLink>
          <NavLink to="/apply" className="nav-link">Apply Now</NavLink>
          <NavLink to="/about" className="nav-link">About</NavLink>
          <NavLink to="/contact" className="nav-link">Contact</NavLink>
        </div>

        <div className="nav-actions">
          {user ? (
            <div className="nav-user">
              <button className="nav-user-btn" onClick={() => setDropdownOpen(!dropdownOpen)}>
                <FiUser size={16} />
                {user.fullName?.split(' ')[0]}
              </button>
              {dropdownOpen && (
                <div className="nav-dropdown">
                  {isAdmin && <Link to="/admin" onClick={() => setDropdownOpen(false)}><FiSettings size={15} /> Admin Panel</Link>}
                  <Link to="/dashboard" onClick={() => setDropdownOpen(false)}><FiUser size={15} /> Dashboard</Link>
                  <div className="nav-dropdown-divider" />
                  <button onClick={handleLogout} className="logout-btn"><FiLogOut size={15} /> Logout</button>
                </div>
              )}
            </div>
          ) : (
            <>
              <Link to="/login" className="btn btn-outline btn-sm">Login</Link>
              <Link to="/apply" className="btn btn-accent btn-sm">Apply Now</Link>
            </>
          )}
        </div>

        <button className="hamburger" onClick={() => setMobileOpen(!mobileOpen)}>
          {mobileOpen ? <FiX /> : <FiMenu />}
        </button>
      </div>

      {mobileOpen && (
        <div className="mobile-nav" onClick={() => setMobileOpen(false)}>
          <NavLink to="/" end>Home</NavLink>
          <NavLink to="/courses">Courses</NavLink>
          <NavLink to="/about">About</NavLink>
          <NavLink to="/contact">Contact</NavLink>
          {user ? (
            <>
              <div className="mobile-divider" />
              {isAdmin && <NavLink to="/admin">Admin Panel</NavLink>}
              <NavLink to="/dashboard">My Dashboard</NavLink>
              <button onClick={handleLogout} style={{ color: 'var(--error)' }}><FiLogOut /> Logout</button>
            </>
          ) : (
            <>
              <div className="mobile-divider" />
              <NavLink to="/login">Login</NavLink>
              <NavLink to="/apply" className="mobile-cta">Apply Now — Free</NavLink>
            </>
          )}
        </div>
      )}
    </nav>
  );
};

export default Navbar;